import {callAPI} from '@/store/utils';
import {extractWalletTokens} from '@/lib/wallet/normalize';

export default {
  namespaced: true,
  state: {
    cardsList: [],
    companyAccounts: [],
    currentCard: null,
    budgetDetails: null,
    walletTokens: [],
  },
  mutations: {
    setCards(state, res) {
      state.cardsList = res?.result || res || [];
    },
    setCurrentCard(state, res) {
      state.currentCard = res?.result || res || null;
    },
    setCompanyAccounts(state, res) {
      state.companyAccounts = res?.result || res || null;
    },
    setBudgetDetails(state, res) {
      state.budgetDetails = res?.result || res || [];
    },
    setWalletTokens(state, tokens) {
      // Receives an already-normalized WalletToken[] (see actionGetWalletTokens).
      state.walletTokens = Array.isArray(tokens) ? tokens : [];
    },
  },
  actions: {
    async actionGetCardsList({commit, rootState}, params = {}) {
      const canViewAll = Boolean(
        rootState.user?.permissions?.manage_all_payment_cards,
      );
      const query = new URLSearchParams();
      if (canViewAll) query.set('view', 'all');

      // TT-6874 — server-side search & filters (backend TT-6843). Only append a
      // param when it carries a value so an empty filter is a genuine no-op.
      const search = String(params.search ?? '').trim();
      if (search) query.set('search', search);
      // `person_id` matches the card owner (user_id) OR its notified user
      // (notified_user_id) — a single "Personne" filter on the frontend.
      if (params.person_id) query.set('person_id', params.person_id);
      if (params.user_id) query.set('user_id', params.user_id);
      if (params.notified_user_id) {
        query.set('notified_user_id', params.notified_user_id);
      }
      if (params.card_type) query.set('card_type', params.card_type);
      if (params.status) query.set('status', params.status);

      const qs = query.toString();
      const res = await callAPI(
        'GET',
        `company/${rootState.companyId}/equals/cards${qs ? `?${qs}` : ''}`,
      );
      commit('setCards', res);
      return res;
    },
    async actionGetCardById({commit, rootState}, cardId) {
      const res = await callAPI(
        'GET',
        `company/${rootState.companyId}/equals/cards/${cardId}`,
      );
      commit('setCurrentCard', res);
      return res;
    },
    async actionCreateCard({rootState}, params) {
      // TT-6732: 'individual_card' (personal) or 'budget_card' (shared). The
      // selected person is the cardholder for an individual card, or the
      // notification main user for a budget/shared card.
      const cardType = params.card_type || 'individual_card';
      const par = {
        card_type: cardType,
        company_id: rootState.companyId,
        name: params.name,
      };
      if (cardType === 'budget_card') {
        par.notified_user_id = params.user_id;
        // PROVISIONAL: the create endpoint does not accept a spending limit yet
        // (limits go through the spending-limits endpoints). Sent so the backend
        // can pick it up once supported; ignored otherwise.
        if (params.spending_limit != null && params.spending_limit !== '') {
          par.spending_limit = Number(params.spending_limit);
        }
      } else {
        par.user_id = params.user_id;
      }

      // TT-6778: card creation is OTP-protected on the backend. Forward the
      // OTP challenge id + entered code for both card types.
      if (params.otp_id) par.otp_id = params.otp_id;
      if (params.code) par.code = params.code;

      return await callAPI('POST', `equals/cards`, {...par});
    },
    // TT-6732 — update the notified user (validator) of a shared/budget card.
    async actionUpdateCardNotifiedUser({rootState}, {cardId, userId}) {
      return await callAPI(
        'PATCH',
        `equals/cards/${cardId}/notified-user`,
        {company_id: rootState.companyId, notified_user_id: userId},
      );
    },
    async actionChangeCardStatus({rootState}, params) {
      const payload = {
        ...params,
        company_id: rootState.companyId,
      };

      return await callAPI(
        'POST',
        `equals/cards/${params.cardId}/transition`,
        payload,
      );
    },
    async actionReplaceCard({rootState}, params) {
      const payload = {
        ...params,
        company_id: rootState.companyId,
      };

      return await callAPI(
        'POST',
        `equals/cards/${params.cardId}/replace`,
        payload,
      );
    },
    async actionConvertToPhysicalCard({rootState}, params) {
      const payload = {
        ...params,
        company_id: rootState.companyId,
      };

      return await callAPI(
        'POST',
        `equals/cards/${params.cardId}/convert-to-physical`,
        payload,
      );
    },
    async actionSetPin({rootState}, params) {
      const payload = {
        ...params,
        company_id: rootState.companyId,
      };
      return await callAPI('PUT', `equals/cards/${params.cardId}/pin`, payload);
    },
    async actionGetClientAccessToken(
      {rootState},
      {cardId, purpose, code = null, otp_id = null},
    ) {
      const payload = {
        purpose,
        ...(code ? {code} : {}),
        ...(otp_id ? {otp_id} : {}),
      };

      return await callAPI(
        'POST',
        `company/${rootState.companyId}/equals/cards/${cardId}/access-token`,
        payload,
      );
    },
    // TT-6891 — digital wallet (Apple/Google) push provisioning + token lifecycle.
    async actionGetWalletTokens({commit, rootState}, cardId) {
      const res = await callAPI(
        'GET',
        `company/${rootState.companyId}/equals/cards/${cardId}/digital-wallet-tokens`,
      );
      // Response is a paginated {count, rows} (or {result:{…}}) — normalize to a
      // flat WalletToken[] the UI can render directly.
      commit('setWalletTokens', extractWalletTokens(res));
      return res;
    },
    // TT-6891 — unified provisioning endpoint for all wallets. `params` carries
    // snake_case body fields: wallet_provider (APPLE|GOOGLE|SAMSUNG), device_type
    // (MOBILE_PHONE|TABLET|WATCH), provisioning_app_version, otp_id + code, plus
    // provider-specific fields — APPLE: certificates/nonce/nonce_signature;
    // GOOGLE|SAMSUNG: device_id/wallet_user_id. Path is company-scoped, so no
    // company_id in the body (mirrors actionGetClientAccessToken).
    async actionProvisionWalletToken({rootState}, params) {
      const {cardId, ...rest} = params;
      return await callAPI(
        'POST',
        `company/${rootState.companyId}/equals/cards/${cardId}/digital-wallet-tokens`,
        rest,
      );
    },
    // TT-6891 — update a token's state on its device: SUSPENDED (pause), ACTIVE
    // (resume), TERMINATED (remove). Company-scoped path → body is just {state}.
    async actionTransitionWalletToken({rootState}, {cardId, tokenId, state}) {
      return await callAPI(
        'POST',
        `company/${rootState.companyId}/equals/cards/${cardId}/digital-wallet-tokens/${tokenId}/transition`,
        {state},
      );
    },
    async actionPost3ds({rootState}, params = {}) {
      const {company_id, ...rest} = params;
      const payload = {
        ...rest,
        // TT-6787: a 3DS challenge can belong to any company the validator
        // manages — not only the currently selected one. Target the operation's
        // own company so the backend can locate the challenge; fall back to the
        // selected company for single-company validators.
        company_id: company_id ?? rootState.companyId,
      };
      const res = await callAPI('POST', `equals/3ds-validation`, payload);
      return res;
    },
    async actionGetCardTransactions({rootState}, params = {}) {
      const payload = {
        ...params,
        company_id: rootState.companyId,
      };

      return await callAPI(
        'POST',
        `equals/cards/${params.cardId}/transactions`,
        payload,
      );
    },
    async actionGetCardSpendingLimits({rootState}, cardId) {
      return await callAPI(
        'GET',
        `company/${rootState.companyId}/equals/cards/${cardId}/spending-limits`,
      );
    },
    async actionUpdateCardSpendingLimit(
      {rootState},
      {cardId, cardLimitId, amount, limit_window, active},
    ) {
      const payload = {
        amount,
        ...(limit_window ? {limit_window} : {}),
        ...(typeof active === 'boolean' ? {active} : {}),
        company_id: rootState.companyId,
      };
      return await callAPI(
        'PUT',
        `company/${rootState.companyId}/equals/cards/${cardId}/spending-limits/${cardLimitId}`,
        payload,
      );
    },
    async actionGetCardTransactionLimits({rootState}, cardId) {
      return await callAPI(
        'GET',
        `company/${rootState.companyId}/equals/cards/${cardId}/transaction-limits`,
      );
    },
    async actionUpdateCardTransactionLimit(
      {rootState},
      {cardId, cardLimitId, amount, active},
    ) {
      const payload = {
        amount,
        ...(typeof active === 'boolean' ? {active} : {}),
        company_id: rootState.companyId,
      };
      return await callAPI(
        'PUT',
        `company/${rootState.companyId}/equals/cards/${cardId}/transaction-limits/${cardLimitId}`,
        payload,
      );
    },
  },
};
